/**
 * Tab container - retains every visited root tab as its own surface.
 * Switching tabs reveals the existing surface instead of rebuilding it.
 *
 * NORTHSTARS:
 * - Less code, better
 * - The retained surface is the cache
 * - One job - render tabs, control visibility
 */

import { Suspense, useState, type ComponentType, type LazyExoticComponent } from "react";
import { Route, Routes } from "react-router-dom";
import { TabActivityProvider } from "@/contexts/TabActivityContext";
import { useActiveScrollContainer } from "@/contexts/ScrollContainerContext";
import ScrollContainer from "@/components/layout/ScrollContainer";
import {
  TAB_ROUTES,
  type TabId,
  type ReselectAction,
} from "@/routes/routeObjects";
import { RouteGround } from "@/components/shared/RouteGround";
import { useTabReselect } from "@/hooks/useTabNavigation";
import { useNavigationStackSafe } from "@/contexts/NavigationStackContext";

interface TabContainerProps {
  activeTab: TabId;
  allowScrollClaim?: boolean;
}

const TAB_SCROLL_MODE: Record<TabId, "auto" | "locked"> = {
  home: "auto",
  stashes: "auto",
  collections: "auto",
  favorites: "auto",
  done: "auto",
  tags: "auto",
  roundups: "locked",
  notifications: "locked",
  events: "locked",
  map: "locked",
  search: "auto",
  profile: "auto",
  discover: "auto",
  explore: "locked",
  chat: "locked",
};

const TabPane = ({
  isActive,
  isMounted,
  tabId,
  routePath,
  reselectAction,
  allowScrollClaim,
  Component,
}: {
  isActive: boolean;
  isMounted: boolean;
  tabId: TabId;
  routePath: string;
  reselectAction: ReselectAction | null;
  allowScrollClaim: boolean;
  Component: LazyExoticComponent<ComponentType<unknown>>;
}) => {
  const context = useNavigationStackSafe();
  const activeScrollContainer = useActiveScrollContainer();
  const scrollMode = TAB_SCROLL_MODE[tabId] ?? "auto";
  const shouldClaimSurface = isActive && allowScrollClaim;

  // Fallback to default location if context not available (edge case during HMR/navigation)
  const tabLocation = context?.getTabLocation(tabId) ?? {
    id: routePath,
    pathname: routePath,
    search: "",
    hash: "",
    state: null,
  };
  useTabReselect(tabId, () => {
    if (reselectAction !== "scrollTop") return;
    (activeScrollContainer ?? window).scrollTo({ top: 0, behavior: "smooth" });
  });

  if (!isMounted) {
    return null;
  }

  const scrollClassName = scrollMode === "locked" ? "overflow-hidden" : "overflow-y-auto overscroll-contain";

  const suspenseFallback = <RouteGround />;

  return (
    <ScrollContainer
      isActive={shouldClaimSurface}
      className={`absolute inset-0 h-full min-h-0 ${scrollClassName}`}
      style={
        isActive
          ? { visibility: "visible" }
          : { visibility: "hidden", pointerEvents: "none" }
      }
      data-tab={tabId}
      // Use inert instead of aria-hidden to properly handle focus.
      // inert removes from a11y tree AND makes descendants non-focusable.
      inert={!isActive}
    >
      <TabActivityProvider tabId={tabId} isActive={isActive}>
        <Suspense fallback={suspenseFallback}>
          <Routes
            location={{
              pathname: tabLocation.pathname,
              search: tabLocation.search,
              hash: tabLocation.hash,
              state: tabLocation.state,
              key: tabLocation.id,
            }}
          >
            <Route path={routePath} element={<Component />} />
          </Routes>
        </Suspense>
      </TabActivityProvider>
    </ScrollContainer>
  );
};

export function TabContainer({ activeTab, allowScrollClaim = true }: TabContainerProps) {
  const [mountedTabs, setMountedTabs] = useState<Set<TabId>>(() => new Set([activeTab]));

  if (!mountedTabs.has(activeTab)) {
    const next = new Set(mountedTabs);
    next.add(activeTab);
    setMountedTabs(next);
  }

  return (
    <>
      {TAB_ROUTES.map(route => {
        const Component = route.handle.component;
        const isActive = route.handle.tabId === activeTab;
        const isMounted = mountedTabs.has(route.handle.tabId);

        return (
          <TabPane
            key={route.path}
            isActive={isActive}
            isMounted={isMounted}
            tabId={route.handle.tabId}
            routePath={route.path}
            reselectAction={route.handle.reselectAction ?? null}
            allowScrollClaim={allowScrollClaim}
            Component={Component}
          />
        );
      })}
    </>
  );
}
